/**
 * <s-story> — plays the hero motion-graphics story in scenes, then loops.
 *
 * Scenes are cumulative boolean attributes on the host (data-discover,
 * data-identify, data-implement, data-improve) plus the current scene name in
 * data-scene. Children style themselves with `group-data-[...]` utilities,
 * so this component only flips attributes on a timer.
 *
 * Liquid renders the host with every scene attribute present, so without
 * JavaScript or with reduced motion the finished scene simply shows.
 */
const SCENES = [
  { name: 'discover', hold: 3000 },
  { name: 'identify', hold: 3400 },
  { name: 'implement', hold: 3400 },
  { name: 'improve', hold: 4800 },
];

class SStory extends HTMLElement {
  #ac;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#ac = new AbortController();
    this.#loop(this.#ac.signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #wait(ms, signal) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    });
  }

  #reset() {
    SCENES.forEach(({ name }) => this.removeAttribute(`data-${name}`));
    this.setAttribute('data-scene', 'reset');
  }

  async #loop(signal) {
    try {
      this.#reset();
      await this.#wait(700, signal);
      while (!signal.aborted) {
        for (const { name, hold } of SCENES) {
          this.setAttribute(`data-${name}`, '');
          this.setAttribute('data-scene', name);
          await this.#wait(hold, signal);
        }
        this.#reset();
        await this.#wait(1600, signal);
      }
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
  }
}

if (!customElements.get('s-story')) {
  customElements.define('s-story', SStory);
}
